import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Typography,
    Box,
    Stack,
    Divider,
    Grid,
    Paper,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Button,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import { GoogleMap, MarkerF, Polyline } from '@react-google-maps/api';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { MobileAppUserTypes } from '../../../types';
import { MobileAppUserService } from '../../../services/mobile-app-user.service';
import type { RootState } from '../../../store';
import { formatOsVersion } from './os-version';

interface Props {
    user: MobileAppUserTypes.Model;
    visible: boolean;
    onClose: () => void;
}

export const MobileAppUserDetailDrawer: React.FC<Props> = ({ user, visible, onClose }) => {
    const [detailedUser, setDetailedUser] = useState<MobileAppUserTypes.Model | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showPushToken, setShowPushToken] = useState<boolean>(false);
    const [currentTab, setCurrentTab] = useState<number>(0);
    const [deviceDetails, setDeviceDetails] = useState<MobileAppUserTypes.DeviceDetailsResponse | null>(null);
    const [deviceError, setDeviceError] = useState<string | null>(null);
    const [loadingDevice, setLoadingDevice] = useState<boolean>(false);
    const [deviceSubTab, setDeviceSubTab] = useState<number>(0);
    const [heartbeats, setHeartbeats] = useState<MobileAppUserTypes.DeviceHeartbeatResponse | null>(null);
    const [heartbeatError, setHeartbeatError] = useState<string | null>(null);
    const [loadingHeartbeats, setLoadingHeartbeats] = useState<boolean>(false);
    const [selectedHeartbeat, setSelectedHeartbeat] = useState<MobileAppUserTypes.DeviceHeartbeat | null>(null);
    const [heartbeatPage, setHeartbeatPage] = useState<number>(1);
    const [iotPackets, setIotPackets] = useState<MobileAppUserTypes.IotPacketResponse | null>(null);
    const [iotPacketError, setIotPacketError] = useState<string | null>(null);
    const [loadingIotPackets, setLoadingIotPackets] = useState<boolean>(false);
    const [selectedIotPacket, setSelectedIotPacket] = useState<MobileAppUserTypes.IotPacket | null>(null);
    const [iotPacketPage, setIotPacketPage] = useState<number>(1);
    const [userLocations, setUserLocations] = useState<MobileAppUserTypes.UserLocationResponse | null>(null);
    const [locationError, setLocationError] = useState<string | null>(null);
    const [loadingLocations, setLoadingLocations] = useState<boolean>(false);
    const [selectedLocation, setSelectedLocation] = useState<MobileAppUserTypes.UserLocation | null>(null);
    const [locationPage, setLocationPage] = useState<number>(1);
    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 39.8283, lng: -98.5795 }); // Default to US center
    const [zoom, setZoom] = useState<number>(10);
    const [permissionTracking, setPermissionTracking] = useState<MobileAppUserTypes.PermissionTrackingData | null>(null);
    const [permissionError, setPermissionError] = useState<string | null>(null);
    const [loadingPermissions, setLoadingPermissions] = useState<boolean>(false);
    const [showRawPermissionData, setShowRawPermissionData] = useState<boolean>(false);
    
    // Edit mode states for Contacts Added field
    const [isEditingContactsAdded, setIsEditingContactsAdded] = useState<boolean>(false);
    const [contactsAddedValue, setContactsAddedValue] = useState<boolean>(false);
    const [originalContactsAddedValue, setOriginalContactsAddedValue] = useState<boolean>(false);
    const [savingContactsAdded, setSavingContactsAdded] = useState<boolean>(false);
    
    const { loading } = useSelector((state: RootState) => state.mobileAppUser);

    // Fetch detailed user data when drawer opens and user ID is available
    useEffect(() => {
        if (visible && user?._id) {
            setDetailedUser(null);
            setError(null);
            setShowPushToken(false); // Reset push token visibility when opening drawer
            setCurrentTab(0); // Reset to first tab when opening drawer
            setDeviceDetails(null); // Reset device details
            setDeviceError(null); // Reset device error
            setDeviceSubTab(0); // Reset device subtab
            setHeartbeats(null); // Reset heartbeats
            setHeartbeatError(null); // Reset heartbeat error
            setSelectedHeartbeat(null); // Reset selected heartbeat
            setHeartbeatPage(1); // Reset heartbeat page
            setIotPackets(null); // Reset IoT packets
            setIotPacketError(null); // Reset IoT packet error
            setSelectedIotPacket(null); // Reset selected IoT packet
            setIotPacketPage(1); // Reset IoT packet page
            setUserLocations(null); // Reset user locations
            setLocationError(null); // Reset location error
            setSelectedLocation(null); // Reset selected location
            setLocationPage(1); // Reset location page
            setPermissionTracking(null); // Reset permission tracking
            setPermissionError(null); // Reset permission error
            setShowRawPermissionData(false); // Reset raw data visibility
            
            MobileAppUserService.getById(user._id, (isValid, message, result) => {
                if (isValid && result) {
                    setDetailedUser(result);
                } else {
                    setError(message || 'Failed to load user details');
                }
            });
        }
    }, [visible, user?._id]);

    // Initialize contacts_added value when detailedUser is loaded
    useEffect(() => {
        if (detailedUser) {
            const currentValue = detailedUser.contacts_added || false;
            setContactsAddedValue(currentValue);
            setOriginalContactsAddedValue(currentValue);
            setIsEditingContactsAdded(false);
        }
    }, [detailedUser]);

    // Fetch device details when Device tab is selected
    useEffect(() => {
        if (currentTab === 1 && user?._id && !deviceDetails && !loadingDevice) {
            setLoadingDevice(true);
            setDeviceError(null);
            
            MobileAppUserService.getDeviceDetails(user._id, (isValid, message, result) => {
                setLoadingDevice(false);
                if (isValid && result) {
                    setDeviceDetails(result);
                } else {
                    setDeviceError(message || 'Failed to load device details');
                }
            });
        }
    }, [currentTab, user?._id, deviceDetails, loadingDevice]);

    // Fetch device heartbeats when Device Heartbeat subtab is selected
    useEffect(() => {
        if (deviceSubTab === 1 && deviceDetails?.device?.macId && !heartbeats && !loadingHeartbeats) {
            setLoadingHeartbeats(true);
            setHeartbeatError(null);
            
            MobileAppUserService.getDeviceHeartbeats(
                deviceDetails.device.macId,
                heartbeatPage,
                50,
                (isValid, message, result) => {
                    setLoadingHeartbeats(false);
                    if (isValid && result) {
                        setHeartbeats(result);
                    } else {
                        setHeartbeatError(message || 'Failed to load device heartbeats');
                    }
                }
            );
        }
    }, [deviceSubTab, deviceDetails?.device?.macId, heartbeats, loadingHeartbeats, heartbeatPage]);

    // Fetch device IoT packets when IoT Packets subtab is selected
    useEffect(() => {
        if (deviceSubTab === 2 && deviceDetails?.device?.macId && !iotPackets && !loadingIotPackets) {
            setLoadingIotPackets(true);
            setIotPacketError(null);
            
            MobileAppUserService.getDeviceIotPackets(
                deviceDetails.device.macId,
                iotPacketPage,
                50,
                (isValid, message, result) => {
                    setLoadingIotPackets(false);
                    if (isValid && result) {
                        setIotPackets(result);
                    } else {
                        setIotPacketError(message || 'Failed to load IoT packets');
                    }
                }
            );
        }
    }, [deviceSubTab, deviceDetails?.device?.macId, iotPackets, loadingIotPackets, iotPacketPage]);

    // Fetch user locations when User Locations tab is selected
    useEffect(() => {
        if (currentTab === 2 && user?._id && !userLocations && !loadingLocations) {
            setLoadingLocations(true);
            setLocationError(null);
            
            MobileAppUserService.getUserLocations(
                user._id,
                locationPage,
                50,
                (isValid, message, result) => {
                    setLoadingLocations(false);
                    if (isValid && result) {
                        setUserLocations(result);
                        
                        // Set map center to the most recent location if available
                        if (result.data.length > 0) {
                            const mostRecentLocation = result.data[0];
                            setMapCenter({
                                lat: mostRecentLocation.coords.latitude,
                                lng: mostRecentLocation.coords.longitude
                            });
                            setZoom(13);
                        }
                    } else {
                        setLocationError(message || 'Failed to load user locations');
                    }
                }
            );
        }
    }, [currentTab, user?._id, userLocations, loadingLocations, locationPage]);

    // Fetch permission tracking when Permission Tracking tab is selected
    useEffect(() => {
        if (currentTab === 3 && user?._id && !permissionTracking && !loadingPermissions) {
            setLoadingPermissions(true);
            setPermissionError(null);
            
            MobileAppUserService.getPermissionTracking(
                user._id,
                (isValid, message, result) => {
                    setLoadingPermissions(false);
                    if (isValid && result) {
                        setPermissionTracking(result);
                    } else {
                        setPermissionError(message || 'Failed to load permission tracking data');
                    }
                }
            );
        }
    }, [currentTab, user?._id, permissionTracking, loadingPermissions]);

    // Use detailed user data if available, otherwise fallback to the user prop
    const displayUser = detailedUser || user;

    const formatDate = (date?: Date | string) => {
        if (!date) return 'N/A';
        return dayjs(date).format('MMM DD, YYYY hh:mm A');
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            // You could add a toast notification here if needed
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    const handleEditContactsAdded = () => {
        setIsEditingContactsAdded(true);
    };

    const handleCancelEditContactsAdded = () => {
        setContactsAddedValue(originalContactsAddedValue);
        setIsEditingContactsAdded(false);
    };

    const handleSaveContactsAdded = async () => {
        if (!detailedUser?._id) return;

        setSavingContactsAdded(true);
        
        try {
            const result = await MobileAppUserService.update(
                detailedUser._id,
                { contacts_added: contactsAddedValue },
                (isValid, message) => {
                    if (isValid) {
                        toast.success('Contacts Added updated successfully', {
                            autoClose: 3000,
                        });
                        setOriginalContactsAddedValue(contactsAddedValue);
                        setIsEditingContactsAdded(false);
                        
                        // Update the detailedUser state with the new value
                        if (detailedUser) {
                            setDetailedUser({
                                ...detailedUser,
                                contacts_added: contactsAddedValue,
                            });
                        }
                    } else {
                        toast.error(message || 'Failed to update Contacts Added', {
                            autoClose: 3000,
                        });
                    }
                }
            );
        } catch (error) {
            toast.error('An error occurred while updating', {
                autoClose: 3000,
            });
        } finally {
            setSavingContactsAdded(false);
        }
    };

    const DetailRow = ({ label, value }: { label: string; value: string | React.ReactNode }) => (
        <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {label}
            </Typography>
            <Typography variant="body1">{value || 'N/A'}</Typography>
        </Box>
    );

    const getAppStatusStyle = (status?: string) => {
        switch (status) {
            case 'ON':
                return { backgroundColor: '#4caf50', color: '#ffffff' };
            case 'OFF':
                return { backgroundColor: '#f44336', color: '#ffffff' };
            default:
                return { backgroundColor: '#9e9e9e', color: '#ffffff' };
        }
    };

    const StatusChip = ({ label }: { label: string }) => {
        const statusStyle = getAppStatusStyle(label);
        return (
            <Box 
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    ...statusStyle
                }}
            >
                {label || 'Unknown'}
            </Box>
        );
    };

    const AdminChip = () => (
        <Box 
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 500,
                backgroundColor: '#1976d2',
                color: '#ffffff'
            }}
        >
            Admin User
        </Box>
    );

    return (
        <Drawer
            anchor="right"
            open={visible}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: '100%', md: '50%' } }
            }}
        >
            <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Mobile App User Details
                </Typography>

                {/* Loading State */}
                {loading.getById && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Error State */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Tabs */}
                {!loading.getById && !error && (
                    <Box>
                        <Tabs
                            value={currentTab}
                            onChange={(_, newValue) => setCurrentTab(newValue)}
                            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
                        >
                            <Tab label="User Information" />
                            <Tab label="User Device" />
                            <Tab label="User Locations" />
                            <Tab label="Permission Tracking" />
                        </Tabs>

                        {/* Tab Content */}
                        {currentTab === 0 && (
                    <Stack spacing={2} divider={<Divider />}>
                    {/* Basic Information */}
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                            Basic Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <DetailRow 
                                    label="Full Name" 
                                    value={`${displayUser.firstName || ''} ${displayUser.lastName || ''}`.trim() || 'N/A'} 
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Phone Number" value={displayUser.phoneNumber} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Employee ID" value={displayUser.employeeId} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Backup Email" value={displayUser.backupEmail} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Customer Name" value={displayUser.customerName} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Department Name" value={displayUser.departmentName} />
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* App Information */}
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                            App Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ py: 1 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        App Status
                                    </Typography>
                                    <StatusChip label={displayUser.appStatus || 'Unknown'} />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="App Version" value={displayUser.appVersion} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow
                                    label="OS Version"
                                    value={displayUser.osVersion ? formatOsVersion(displayUser.platform, displayUser.osVersion) : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Connection Code" value={displayUser.connectionCode} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Opted In" value={displayUser.optedIn ? 'Yes' : 'No'} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Debug Mode" value={displayUser.debugMode ? 'Enabled' : 'Disabled'} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Tutorial Completed" value={displayUser.tutorialCompleted ? 'Yes' : 'No'} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Terms Accepted" value={displayUser.termsAndConditionsAccepted ? 'Yes' : 'No'} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Privacy Policy Accepted" value={displayUser.privacyPolicyAccepted ? 'Yes' : 'No'} />
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* App Version Details */}
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                            App Version Details
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Current App Version" value={displayUser.appVersion} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Last Notification Version" value={displayUser.appVersionLastNotification} />
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Location Information */}
                    {displayUser.mobileLocation && (
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                Location Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <DetailRow label="Address" value={displayUser.mobileLocation.address} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailRow label="Latitude" value={displayUser.mobileLocation.latitude} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailRow label="Longitude" value={displayUser.mobileLocation.longitude} />
                                </Grid>
                                <Grid item xs={12}>
                                    <DetailRow 
                                        label="Location Updated" 
                                        value={formatDate(displayUser.mobileLocation.updatedAt)} 
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    )}

                    {/* Settings Information */}
                    {displayUser.settings && (
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                App Settings
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <DetailRow 
                                        label="Location Sharing" 
                                        value={displayUser.settings.location_sharing ? 'Enabled' : 'Disabled'} 
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailRow 
                                        label="Notification Type" 
                                        value={displayUser.settings.notification_type || 'N/A'} 
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailRow 
                                        label="Location Tracking" 
                                        value={displayUser.settings.location_tracking_setting || 'N/A'} 
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailRow 
                                        label="Fake Call Timer (min)" 
                                        value={displayUser.settings.default_timer_until_fake_phone_call_duration_timer?.toString()} 
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailRow 
                                        label="Check On Me Timer (min)" 
                                        value={displayUser.settings.default_check_on_me_duration_timer?.toString()} 
                                    />
                                </Grid>
                                {displayUser.settings.work_schedule && (
                                    <Grid item xs={12} md={6}>
                                        <DetailRow 
                                            label="Work Schedule Timezone" 
                                            value={displayUser.settings.work_schedule.timezone} 
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>
                    )}

                    {/* SOS Settings */}
                    {displayUser.sosSettings && (
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                SOS Settings
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <DetailRow 
                                        label="Monitoring Enabled" 
                                        value={displayUser.sosSettings.monitoring ? 'Yes' : 'No'} 
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailRow 
                                        label="Monitoring Service" 
                                        value={displayUser.sosSettings.monitoringService} 
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailRow 
                                        label="Fake Call Enabled" 
                                        value={displayUser.sosSettings.fakeCallEnabled ? 'Yes' : 'No'} 
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailRow 
                                        label="Alert SOS Connections" 
                                        value={displayUser.sosSettings.alertSOSConnections ? 'Yes' : 'No'} 
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailRow 
                                        label="Monitoring Phone" 
                                        value={displayUser.sosSettings.monitoringPhone} 
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    )}

                    {/* Action Event Settings */}
                    {displayUser.actionEventSettings && (
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                Action Event Settings
                            </Typography>
                            <Box 
                                sx={{ 
                                    bgcolor: '#f5f5f5',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    p: 2,
                                    overflow: 'auto',
                                    maxHeight: 300
                                }}
                            >
                                <Typography 
                                    variant="body2" 
                                    component="pre"
                                    sx={{ 
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        margin: 0,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all'
                                    }}
                                >
                                    {JSON.stringify(displayUser.actionEventSettings, null, 2)}
                                </Typography>
                            </Box>
                        </Paper>
                    )}

                    {/* Migration & Contact Status */}
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                Migration & Contact Status
                            </Typography>
                            {!isEditingContactsAdded && (
                                <Tooltip title="Edit Contacts Added">
                                    <IconButton 
                                        size="small" 
                                        onClick={handleEditContactsAdded}
                                        sx={{ 
                                            bgcolor: 'primary.main', 
                                            color: 'white',
                                            '&:hover': { bgcolor: 'primary.dark' }
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <DetailRow 
                                    label="Is Migrated" 
                                    value={displayUser.isMigrated ? 'Yes' : 'No'} 
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {isEditingContactsAdded ? (
                                    <Box sx={{ py: 1 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Contacts Added
                                        </Typography>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={contactsAddedValue ? 'yes' : 'no'}
                                                onChange={(e) => setContactsAddedValue(e.target.value === 'yes')}
                                                sx={{ bgcolor: 'white' }}
                                            >
                                                <MenuItem value="yes">Yes</MenuItem>
                                                <MenuItem value="no">No</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                ) : (
                                    <DetailRow 
                                        label="Contacts Added" 
                                        value={displayUser.contacts_added ? 'Yes' : 'No'} 
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <DetailRow 
                                    label="Contacts Added Timestamp" 
                                    value={displayUser.contacts_added_timestamp ? formatDate(displayUser.contacts_added_timestamp) : 'N/A'} 
                                />
                            </Grid>
                        </Grid>
                        
                        {/* Save and Cancel buttons - only show when editing and value changed */}
                        {isEditingContactsAdded && contactsAddedValue !== originalContactsAddedValue && (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={handleCancelEditContactsAdded}
                                    disabled={savingContactsAdded}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    variant="contained" 
                                    size="small"
                                    onClick={handleSaveContactsAdded}
                                    disabled={savingContactsAdded}
                                >
                                    {savingContactsAdded ? 'Saving...' : 'Save'}
                                </Button>
                            </Box>
                        )}
                    </Paper>

                    {/* Detailed Work Schedule */}
                    {displayUser.settings?.work_schedule && (
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                Detailed Work Schedule
                            </Typography>
                            <Box 
                                sx={{ 
                                    bgcolor: '#f5f5f5',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    p: 2,
                                    overflow: 'auto',
                                    maxHeight: 400
                                }}
                            >
                                <Typography 
                                    variant="body2" 
                                    component="pre"
                                    sx={{ 
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        margin: 0,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all'
                                    }}
                                >
                                    {JSON.stringify(displayUser.settings.work_schedule, null, 2)}
                                </Typography>
                            </Box>
                        </Paper>
                    )}

                    {/* System Information */}
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                            System Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <DetailRow label="User ID" value={displayUser._id} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Subscription ID" value={displayUser.subscriptionId} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Subscription Invite Code" value={displayUser.subscriptionInviteCode} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Invite Code" value={displayUser.inviteCode} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Pass Code" value={displayUser.passCode} />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ py: 1 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Push Token
                                    </Typography>
                                    {displayUser.pushToken ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {showPushToken ? (
                                                <>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            fontFamily: 'monospace',
                                                            fontSize: '0.75rem',
                                                            bgcolor: '#f5f5f5',
                                                            p: 1,
                                                            borderRadius: 1,
                                                            wordBreak: 'break-all',
                                                            flex: 1
                                                        }}
                                                    >
                                                        {displayUser.pushToken}
                                                    </Typography>
                                                    <Tooltip title="Copy Token">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => copyToClipboard(displayUser.pushToken!)}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Hide Token">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setShowPushToken(false)}
                                                        >
                                                            <VisibilityOffIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography variant="body1">Available</Typography>
                                                    <Tooltip title="Show Token">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setShowPushToken(true)}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Box>
                                    ) : (
                                        <Typography variant="body1">N/A</Typography>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Primary Device Phone" value={displayUser.primaryDevicePhoneNumber} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Created" value={formatDate(displayUser.createdAt)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DetailRow label="Last Updated" value={formatDate(displayUser.updatedAt)} />
                            </Grid>
                            {displayUser.isAdmin && (
                                <Grid item xs={12}>
                                    <Box sx={{ py: 1 }}>
                                        <AdminChip />
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                    </Stack>
                        )}

                        {/* User Device Tab */}
                        {currentTab === 1 && (
                            <Box>
                                {/* Device Subtabs - only show if device exists */}
                                {!loadingDevice && !deviceError && deviceDetails?.device && (
                                    <Box>
                                        <Tabs 
                                            value={deviceSubTab} 
                                            onChange={(_, newValue) => setDeviceSubTab(newValue)}
                                            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                                        >
                                            <Tab label="Device Information" />
                                            <Tab label="Device Heartbeats" />
                                            <Tab label="IoT Packets" />
                                        </Tabs>
                                    </Box>
                                )}
                                
                                {/* Device Subtab Content */}
                                {/* Loading State */}
                                {loadingDevice && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                )}

                                {/* Error State */}
                                {deviceError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {deviceError}
                                    </Alert>
                                )}

                                {/* No Device State */}
                                {!loadingDevice && !deviceError && deviceDetails && !deviceDetails.device && (
                                    <Box sx={{ py: 3, textAlign: 'center' }}>
                                        <Typography variant="h6" color="text.secondary">
                                            No Device Found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            This user does not have an associated device.
                                        </Typography>
                                    </Box>
                                )}

                                {/* Device Information Subtab */}
                                {!loadingDevice && !deviceError && deviceDetails?.device && deviceSubTab === 0 && (
                                    <Stack spacing={2} divider={<Divider />}>
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                                Basic Device Information
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow label="Serial Number" value={deviceDetails.device.serialNumber} />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow label="Model" value={deviceDetails.device.model} />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow label="Name" value={deviceDetails.device.name} />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow label="MAC ID" value={deviceDetails.device.macId} />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow label="ICCID" value={deviceDetails.device.iccid} />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow label="Firmware Version" value={deviceDetails.device.firmwareVersion} />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow label="Created" value={formatDate(deviceDetails.device.createdAt)} />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow label="Last Updated" value={formatDate(deviceDetails.device.updatedAt)} />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow label="Device ID" value={deviceDetails.device._id} />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow label="Subscription ID" value={deviceDetails.device.subscriptionId} />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow label="User ID" value={deviceDetails.device.userId} />
                                                </Grid>
                                            </Grid>
                                        </Paper>

                                        {/* Device Status */}
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                                Device Status
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow 
                                                        label="Connected" 
                                                        value={deviceDetails.device.connected ? 'Yes' : 'No'} 
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow 
                                                        label="Terminated" 
                                                        value={deviceDetails.device.terminated ? 'Yes' : 'No'} 
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow 
                                                        label="Activated" 
                                                        value={deviceDetails.device.activated ? 'Yes' : 'No'} 
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow label="Activation Status" value={deviceDetails.device.activationStatus} />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow 
                                                        label="In Activation Queue" 
                                                        value={deviceDetails.device.inActivationQueue ? 'Yes' : 'No'} 
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow 
                                                        label="Activation Notification Sent" 
                                                        value={deviceDetails.device.activationNotificationSent ? 'Yes' : 'No'} 
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <DetailRow 
                                                        label="Activation Completed" 
                                                        value={formatDate(deviceDetails.device.activationCompletedAt)} 
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Paper>

                                        {/* Device Metrics */}
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                                Device Metrics
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow 
                                                        label="Battery Level" 
                                                        value={`${deviceDetails.device.batteryLevel}%`} 
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow 
                                                        label="Bluetooth Signal (RSSI)" 
                                                        value={`${deviceDetails.device.bluetoothSignalStrength.rssi} dBm`} 
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Paper>

                                        {/* Last Location */}
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                                Last Known Location
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow 
                                                        label="Latitude" 
                                                        value={deviceDetails.device.lastLocation.lat} 
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <DetailRow 
                                                        label="Longitude" 
                                                        value={deviceDetails.device.lastLocation.lng} 
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Paper>

                                        {/* Device Configuration */}
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                                Device Configuration
                                            </Typography>
                                            <Box 
                                                sx={{ 
                                                    bgcolor: '#f5f5f5',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 1,
                                                    p: 2,
                                                    overflow: 'auto',
                                                    maxHeight: 300
                                                }}
                                            >
                                                <Typography 
                                                    variant="body2" 
                                                    component="pre"
                                                    sx={{ 
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.75rem',
                                                        margin: 0,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-all'
                                                    }}
                                                >
                                                    {JSON.stringify(deviceDetails.device.deviceConfig, null, 2)}
                                                </Typography>
                                            </Box>
                                        </Paper>

                                        {/* Device Action Event Settings */}
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                                Device Action Event Settings
                                            </Typography>
                                            <Box 
                                                sx={{ 
                                                    bgcolor: '#f5f5f5',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 1,
                                                    p: 2,
                                                    overflow: 'auto',
                                                    maxHeight: 300
                                                }}
                                            >
                                                <Typography 
                                                    variant="body2" 
                                                    component="pre"
                                                    sx={{ 
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.75rem',
                                                        margin: 0,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-all'
                                                    }}
                                                >
                                                    {JSON.stringify(deviceDetails.device.actionEventSettings, null, 2)}
                                                </Typography>
                                            </Box>
                                        </Paper>

                                        {/* Manufacturing Test Results */}
                                        {deviceDetails.manufacturingTestResult && (
                                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                                    Manufacturing Test Results
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={6}>
                                                        <DetailRow 
                                                            label="Test Result" 
                                                            value={deviceDetails.manufacturingTestResult.testResult} 
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <DetailRow 
                                                            label="Test Exit Code" 
                                                            value={deviceDetails.manufacturingTestResult.testExitCode} 
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <DetailRow 
                                                            label="Test Date" 
                                                            value={deviceDetails.manufacturingTestResult.testDateTime} 
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <DetailRow 
                                                            label="MAC Address" 
                                                            value={deviceDetails.manufacturingTestResult.macAddress} 
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <DetailRow 
                                                            label="Test Phone Number" 
                                                            value={deviceDetails.manufacturingTestResult.phoneNumber} 
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <DetailRow 
                                                            label="Test Updated" 
                                                            value={formatDate(deviceDetails.manufacturingTestResult.updateAt)} 
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <DetailRow 
                                                            label="Test Record ID" 
                                                            value={deviceDetails.manufacturingTestResult._id} 
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <DetailRow 
                                                            label="Test Serial Number" 
                                                            value={deviceDetails.manufacturingTestResult.serialNumber} 
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <DetailRow 
                                                            label="Test ICCID" 
                                                            value={deviceDetails.manufacturingTestResult.iccid} 
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        )}
                                    </Stack>
                                )}

                                {/* Device Heartbeat Subtab */}
                                {!loadingDevice && !deviceError && deviceDetails?.device && deviceSubTab === 1 && (
                                    <Box>
                                        {/* Heartbeat Loading State */}
                                        {loadingHeartbeats && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                                <CircularProgress />
                                            </Box>
                                        )}

                                        {/* Heartbeat Error State */}
                                        {heartbeatError && (
                                            <Alert severity="error" sx={{ mb: 2 }}>
                                                {heartbeatError}
                                            </Alert>
                                        )}

                                        {/* Selected Heartbeat Detail */}
                                        {selectedHeartbeat && (
                                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                        Heartbeat Details
                                                    </Typography>
                                                    <Button 
                                                        size="small" 
                                                        onClick={() => setSelectedHeartbeat(null)}
                                                    >
                                                        Close
                                                    </Button>
                                                </Box>
                                                <Box 
                                                    sx={{ 
                                                        bgcolor: '#f5f5f5',
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 1,
                                                        p: 2,
                                                        overflow: 'auto',
                                                        maxHeight: 400
                                                    }}
                                                >
                                                    <Typography 
                                                        variant="body2" 
                                                        component="pre"
                                                        sx={{ 
                                                            fontFamily: 'monospace',
                                                            fontSize: '0.75rem',
                                                            margin: 0,
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-all'
                                                        }}
                                                    >
                                                        {JSON.stringify(selectedHeartbeat, null, 2)}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        )}

                                        {/* Heartbeat Table */}
                                        {!loadingHeartbeats && !heartbeatError && heartbeats && (
                                            <Box>
                                                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                                                    <Table stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Timestamp</TableCell>
                                                                <TableCell>Battery Level</TableCell>
                                                                <TableCell>Location</TableCell>
                                                                <TableCell>RSRQ/RSRP</TableCell>
                                                                <TableCell>Actions</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {heartbeats.data.map((heartbeat) => (
                                                                <TableRow 
                                                                    key={heartbeat._id}
                                                                    hover
                                                                    sx={{ cursor: 'pointer' }}
                                                                    onClick={() => setSelectedHeartbeat(heartbeat)}
                                                                >
                                                                    <TableCell>
                                                                        {formatDate(heartbeat.timestamp)}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {typeof heartbeat.batteryLevel === 'number' ? `${heartbeat.batteryLevel}%` : 'N/A'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box sx={{ maxWidth: 200 }}>
                                                                            <Typography variant="body2" noWrap>
                                                                                {heartbeat.mobileLocation?.address || 
                                                                                 (typeof heartbeat.gpsn === 'number' && typeof heartbeat.gpsw === 'number'
                                                                                    ? `${heartbeat.gpsn.toFixed(4)}, ${heartbeat.gpsw.toFixed(4)}`
                                                                                    : 'No location data')}
                                                                            </Typography>
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {(typeof heartbeat.rsrq === 'number' ? heartbeat.rsrq : 'N/A')}/{(typeof heartbeat.rsrp === 'number' ? heartbeat.rsrp : 'N/A')}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Button 
                                                                            size="small" 
                                                                            variant="outlined"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setSelectedHeartbeat(heartbeat);
                                                                            }}
                                                                        >
                                                                            View Details
                                                                        </Button>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                                
                                                {/* Pagination - Outside table container to ensure visibility */}
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'center',
                                                    mt: 1,
                                                    p: 1,
                                                    bgcolor: 'background.paper',
                                                    borderTop: 1,
                                                    borderColor: 'divider'
                                                }}>
                                                    <TablePagination
                                                        component="div"
                                                        count={heartbeats.pagination.total}
                                                        page={heartbeatPage - 1}
                                                        onPageChange={(_, newPage) => {
                                                            setHeartbeatPage(newPage + 1);
                                                            setHeartbeats(null); // Reset to trigger refetch
                                                        }}
                                                        rowsPerPage={50}
                                                        rowsPerPageOptions={[50]}
                                                        sx={{ 
                                                            '.MuiTablePagination-toolbar': {
                                                                minHeight: '52px'
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        )}

                                        {/* No Heartbeats State */}
                                        {!loadingHeartbeats && !heartbeatError && heartbeats && heartbeats.data.length === 0 && (
                                            <Box sx={{ py: 3, textAlign: 'center' }}>
                                                <Typography variant="h6" color="text.secondary">
                                                    No Heartbeats Found
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    This device has no heartbeat records.
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {/* IoT Packets Subtab */}
                                {!loadingDevice && !deviceError && deviceDetails?.device && deviceSubTab === 2 && (
                                    <Box>
                                        {/* IoT Packet Loading State */}
                                        {loadingIotPackets && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                                <CircularProgress />
                                            </Box>
                                        )}

                                        {/* IoT Packet Error State */}
                                        {iotPacketError && (
                                            <Alert severity="error" sx={{ mb: 2 }}>
                                                {iotPacketError}
                                            </Alert>
                                        )}

                                        {/* Selected IoT Packet Detail */}
                                        {selectedIotPacket && (
                                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                        IoT Packet Details
                                                    </Typography>
                                                    <Button 
                                                        size="small" 
                                                        onClick={() => setSelectedIotPacket(null)}
                                                    >
                                                        Close
                                                    </Button>
                                                </Box>
                                                <Box 
                                                    sx={{ 
                                                        bgcolor: '#f5f5f5',
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 1,
                                                        p: 2,
                                                        overflow: 'auto',
                                                        maxHeight: 400
                                                    }}
                                                >
                                                    <Typography 
                                                        variant="body2" 
                                                        component="pre"
                                                        sx={{ 
                                                            fontFamily: 'monospace',
                                                            fontSize: '0.75rem',
                                                            margin: 0,
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-all'
                                                        }}
                                                    >
                                                        {JSON.stringify(selectedIotPacket, null, 2)}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        )}

                                        {/* IoT Packets Table */}
                                        {!loadingIotPackets && !iotPacketError && iotPackets && (
                                            <Box>
                                                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                                                    <Table stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Timestamp</TableCell>
                                                                <TableCell>Header/Type</TableCell>
                                                                <TableCell>Version</TableCell>
                                                                <TableCell>Battery Level</TableCell>
                                                                <TableCell>GPS Location</TableCell>
                                                                <TableCell>Signal (RSRQ/RSRP)</TableCell>
                                                                <TableCell>Actions</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {iotPackets.data.map((packet) => (
                                                                <TableRow 
                                                                    key={packet._id}
                                                                    hover
                                                                    sx={{ cursor: 'pointer' }}
                                                                    onClick={() => setSelectedIotPacket(packet)}
                                                                >
                                                                    <TableCell>
                                                                        {formatDate(packet.timestamp)}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box>
                                                                            <Typography variant="body2">{packet.header}</Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Type: {packet.type}
                                                                            </Typography>
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {packet.majorVersion}.{packet.minorVersion}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {typeof packet.batteryLevel === 'number' ? `${packet.batteryLevel}%` : 'N/A'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box sx={{ maxWidth: 200 }}>
                                                                            <Typography variant="body2" noWrap>
                                                                                {packet.gpsn && packet.gpsw 
                                                                                    ? `${parseFloat(packet.gpsn).toFixed(4)}, ${parseFloat(packet.gpsw).toFixed(4)}`
                                                                                    : 'No location data'}
                                                                            </Typography>
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {(typeof packet.rsrq === 'number' ? packet.rsrq : 'N/A')}/{(typeof packet.rsrp === 'number' ? packet.rsrp : 'N/A')}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Button 
                                                                            size="small" 
                                                                            variant="outlined"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setSelectedIotPacket(packet);
                                                                            }}
                                                                        >
                                                                            View Details
                                                                        </Button>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                                
                                                {/* Pagination - Outside table container to ensure visibility */}
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'center',
                                                    mt: 1,
                                                    p: 1,
                                                    bgcolor: 'background.paper',
                                                    borderTop: 1,
                                                    borderColor: 'divider'
                                                }}>
                                                    <TablePagination
                                                        component="div"
                                                        count={iotPackets.pagination.total}
                                                        page={iotPacketPage - 1}
                                                        onPageChange={(_, newPage) => {
                                                            setIotPacketPage(newPage + 1);
                                                            setIotPackets(null); // Reset to trigger refetch
                                                        }}
                                                        rowsPerPage={50}
                                                        rowsPerPageOptions={[50]}
                                                        sx={{ 
                                                            '.MuiTablePagination-toolbar': {
                                                                minHeight: '52px'
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        )}

                                        {/* No IoT Packets State */}
                                        {!loadingIotPackets && !iotPacketError && iotPackets && iotPackets.data.length === 0 && (
                                            <Box sx={{ py: 3, textAlign: 'center' }}>
                                                <Typography variant="h6" color="text.secondary">
                                                    No IoT Packets Found
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    This device has no IoT packet records.
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* User Locations Tab */}
                        {currentTab === 2 && (
                            <Box>
                                {/* Location Loading State */}
                                {loadingLocations && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                )}

                                {/* Location Error State */}
                                {locationError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {locationError}
                                    </Alert>
                                )}

                                {/* Selected Location Detail */}
                                {selectedLocation && (
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                Location Details
                                            </Typography>
                                            <Button 
                                                size="small" 
                                                onClick={() => setSelectedLocation(null)}
                                            >
                                                Close
                                            </Button>
                                        </Box>
                                        <Box 
                                            sx={{ 
                                                bgcolor: '#f5f5f5',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 1,
                                                p: 2,
                                                overflow: 'auto',
                                                maxHeight: 400
                                            }}
                                        >
                                            <Typography 
                                                variant="body2" 
                                                component="pre"
                                                sx={{ 
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.75rem',
                                                    margin: 0,
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-all'
                                                }}
                                            >
                                                {JSON.stringify(selectedLocation, null, 2)}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                )}

                                {/* Map Display */}
                                {!loadingLocations && !locationError && userLocations && userLocations.data.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Location Map ({userLocations.pagination.total} locations)
                                        </Typography>
                                        <Box sx={{ height: '400px', width: '100%' }}>
                                            <GoogleMap
                                                mapContainerStyle={{ height: '100%', width: '100%' }}
                                                center={mapCenter}
                                                zoom={zoom}
                                                options={{
                                                    disableDefaultUI: true,
                                                    zoomControl: true,
                                                    fullscreenControl: true,
                                                    mapTypeControl: true,
                                                    streetViewControl: true,
                                                    scaleControl: true,
                                                    clickableIcons: false,
                                                    styles: [
                                                        {
                                                            featureType: 'poi',
                                                            elementType: 'labels',
                                                            stylers: [{ visibility: 'off' }],
                                                        },
                                                    ],
                                                }}
                                            >
                                                {/* Polyline to show movement path */}
                                                {userLocations.data.length > 1 && (
                                                    <Polyline
                                                        path={[...userLocations.data]
                                                            .reverse() // Reverse to get chronological order (oldest to newest)
                                                            .map(location => ({
                                                                lat: location.coords.latitude,
                                                                lng: location.coords.longitude
                                                            }))}
                                                        options={{
                                                            strokeColor: '#2196F3',
                                                            strokeOpacity: 0.8,
                                                            strokeWeight: 3,
                                                            geodesic: true,
                                                        }}
                                                    />
                                                )}

                                                {userLocations.data.map((location, index) => (
                                                    <MarkerF
                                                        key={location._id}
                                                        position={{
                                                            lat: location.coords.latitude,
                                                            lng: location.coords.longitude
                                                        }}
                                                        title={`${formatDate(location.timestamp)} - ${location.locationType}`}
                                                        onClick={() => {
                                                            setSelectedLocation(location);
                                                            setMapCenter({
                                                                lat: location.coords.latitude,
                                                                lng: location.coords.longitude
                                                            });
                                                        }}
                                                        icon={{
                                                            url: index === 0 ? 
                                                                'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ff0000"%3E%3Cpath d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/%3E%3C/svg%3E' :
                                                                'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%232196f3"%3E%3Cpath d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/%3E%3C/svg%3E',
                                                            scaledSize: new window.google.maps.Size(30, 30),
                                                            anchor: new window.google.maps.Point(15, 30)
                                                        }}
                                                    />
                                                ))}
                                            </GoogleMap>
                                        </Box>
                                    </Box>
                                )}

                                {/* Locations Table */}
                                {!loadingLocations && !locationError && userLocations && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Location History
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                                            <Table stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Timestamp</TableCell>
                                                        <TableCell>Location Type</TableCell>
                                                        <TableCell>Coordinates</TableCell>
                                                        <TableCell>Accuracy</TableCell>
                                                        <TableCell>Battery</TableCell>
                                                        <TableCell>Movement</TableCell>
                                                        <TableCell>Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {userLocations.data.map((location) => (
                                                        <TableRow 
                                                            key={location._id}
                                                            hover
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={() => setSelectedLocation(location)}
                                                        >
                                                            <TableCell>
                                                                {formatDate(location.timestamp)}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box 
                                                                    sx={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        borderRadius: 1,
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: 600,
                                                                        backgroundColor: location.locationType === 'heartbeat' ? '#1976d2' : '#9c27b0',
                                                                        color: '#ffffff'
                                                                    }}
                                                                >
                                                                    {location.locationType}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                                    {location.coords.latitude.toFixed(6)}, {location.coords.longitude.toFixed(6)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                {location.coords.accuracy.toFixed(1)}m
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography variant="body2">
                                                                        {Math.round(location.battery.level * 100)}%
                                                                    </Typography>
                                                                    {location.battery.is_charging && (
                                                                        <Box 
                                                                            sx={{
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                px: 1,
                                                                                py: 0.25,
                                                                                borderRadius: 0.5,
                                                                                fontSize: '0.6rem',
                                                                                fontWeight: 600,
                                                                                backgroundColor: '#4caf50',
                                                                                color: '#ffffff'
                                                                            }}
                                                                        >
                                                                            Charging
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box 
                                                                    sx={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        px: 1,
                                                                        py: 0.25,
                                                                        borderRadius: 0.5,
                                                                        fontSize: '0.6rem',
                                                                        fontWeight: 600,
                                                                        backgroundColor: location.is_moving ? '#ff9800' : '#2196f3',
                                                                        color: '#ffffff'
                                                                    }}
                                                                >
                                                                    {location.is_moving ? 'Moving' : 'Stationary'}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Button 
                                                                    size="small" 
                                                                    variant="outlined"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedLocation(location);
                                                                        setMapCenter({
                                                                            lat: location.coords.latitude,
                                                                            lng: location.coords.longitude
                                                                        });
                                                                    }}
                                                                >
                                                                    View Details
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        
                                        {/* Pagination */}
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'center',
                                            mt: 1,
                                            p: 1,
                                            bgcolor: 'background.paper',
                                            borderTop: 1,
                                            borderColor: 'divider'
                                        }}>
                                            <TablePagination
                                                component="div"
                                                count={userLocations.pagination.total}
                                                page={locationPage - 1}
                                                onPageChange={(_, newPage) => {
                                                    setLocationPage(newPage + 1);
                                                    setUserLocations(null); // Reset to trigger refetch
                                                }}
                                                rowsPerPage={50}
                                                rowsPerPageOptions={[50]}
                                                sx={{ 
                                                    '.MuiTablePagination-toolbar': {
                                                        minHeight: '52px'
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                )}

                                {/* No Locations State */}
                                {!loadingLocations && !locationError && userLocations && userLocations.data.length === 0 && (
                                    <Box sx={{ py: 3, textAlign: 'center' }}>
                                        <Typography variant="h6" color="text.secondary">
                                            No Locations Found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            This user has no location records.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Permission Tracking Tab */}
                        {currentTab === 3 && (
                            <Box>
                                {/* Permission Tracking Loading State */}
                                {loadingPermissions && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                )}

                                {/* Permission Tracking Error State */}
                                {permissionError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {permissionError}
                                    </Alert>
                                )}

                                {/* Permission Tracking Content */}
                                {!loadingPermissions && !permissionError && permissionTracking && (
                                    <Stack spacing={3}>
                                        {/* Overview Section */}
                                        {permissionTracking.state._id && (
                                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                                    Current Tracking Status
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Platform:
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                                                            {permissionTracking.state.platform}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Last Screen Seen:
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {permissionTracking.state.lastScreenSeen}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Last Interaction:
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {formatDate(permissionTracking.state.lastInteractionTime)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Current Flow ID:
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                            {permissionTracking.state.currentFlowId}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        )}

                                        {/* Permission Status Summary */}
                                        {permissionTracking.state._id && (
                                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                                    Permission Status Summary
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    {Object.entries(permissionTracking.state.lastSeenDates || {}).map(([permissionType, lastSeen]) => {
                                                        const viewCount = permissionTracking.state.viewCounts?.[permissionType] || 0;
                                                        const latestSession = permissionTracking.sessions.find(s => s.screenType === permissionType);
                                                        const finalState = latestSession?.finalPermissionState;
                                                        
                                                        return (
                                                            <Grid item xs={12} sm={6} md={4} key={permissionType}>
                                                                <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                                                    <Typography variant="body2" fontWeight={600} gutterBottom sx={{ textTransform: 'capitalize' }}>
                                                                        {permissionType.replace(/([A-Z])/g, ' $1').trim()}
                                                                    </Typography>
                                                                    <Stack spacing={1}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Typography variant="caption" color="text.secondary">Status:</Typography>
                                                                            <Box 
                                                                                sx={{
                                                                                    display: 'inline-flex',
                                                                                    alignItems: 'center',
                                                                                    px: 1,
                                                                                    py: 0.25,
                                                                                    borderRadius: 0.5,
                                                                                    fontSize: '0.65rem',
                                                                                    fontWeight: 600,
                                                                                    textTransform: 'uppercase',
                                                                                    backgroundColor: finalState === 'granted' ? '#4caf50' : finalState === 'denied' ? '#f44336' : '#ff9800',
                                                                                    color: '#ffffff'
                                                                                }}
                                                                            >
                                                                                {finalState || 'Unknown'}
                                                                            </Box>
                                                                        </Box>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Last seen: {formatDate(lastSeen)}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            View count: {viewCount}
                                                                        </Typography>
                                                                    </Stack>
                                                                </Box>
                                                            </Grid>
                                                        );
                                                    })}
                                                </Grid>
                                            </Paper>
                                        )}

                                        {/* Session History */}
                                        {permissionTracking.sessions.length > 0 && (
                                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                                    Session History ({permissionTracking.sessions.length} sessions)
                                                </Typography>
                                                <TableContainer sx={{ maxHeight: 400 }}>
                                                    <Table size="small" stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Permission Type</TableCell>
                                                                <TableCell>Start Time</TableCell>
                                                                <TableCell>Duration</TableCell>
                                                                <TableCell>Result</TableCell>
                                                                <TableCell>State Change</TableCell>
                                                                <TableCell>Platform</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {permissionTracking.sessions
                                                                .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
                                                                .map((session) => (
                                                                <TableRow key={session._id} hover>
                                                                    <TableCell>
                                                                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                                            {session.screenType.replace(/([A-Z])/g, ' $1').trim()}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2">
                                                                            {formatDate(session.startTime)}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2">
                                                                            {session.timeSpentSeconds}s
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box 
                                                                            sx={{
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                px: 1,
                                                                                py: 0.25,
                                                                                borderRadius: 0.5,
                                                                                fontSize: '0.6rem',
                                                                                fontWeight: 600,
                                                                                backgroundColor: session.result.includes('Granted') ? '#4caf50' : '#f44336',
                                                                                color: '#ffffff'
                                                                            }}
                                                                        >
                                                                            {session.result}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                                                            {session.initialPermissionState} → {session.finalPermissionState}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                                                                            {session.platform}
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Paper>
                                        )}

                                        {/* Raw Data Section */}
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    Raw Permission Data
                                                </Typography>
                                                <Button 
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setShowRawPermissionData(!showRawPermissionData)}
                                                >
                                                    {showRawPermissionData ? 'Hide' : 'Show'} Raw Data
                                                </Button>
                                            </Box>
                                            {showRawPermissionData && (
                                                <Box 
                                                    sx={{ 
                                                        bgcolor: '#f5f5f5',
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 1,
                                                        p: 2,
                                                        overflow: 'auto',
                                                        maxHeight: 600
                                                    }}
                                                >
                                                    <Typography 
                                                        variant="body2" 
                                                        component="pre"
                                                        sx={{ 
                                                            fontFamily: 'monospace',
                                                            fontSize: '0.7rem',
                                                            margin: 0,
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-all'
                                                        }}
                                                    >
                                                        {JSON.stringify(permissionTracking, null, 2)}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Paper>
                                    </Stack>
                                )}

                                {/* No Permission Tracking Data State */}
                                {!loadingPermissions && !permissionError && permissionTracking && !permissionTracking.state._id && (
                                    <Box sx={{ py: 3, textAlign: 'center' }}>
                                        <Typography variant="h6" color="text.secondary">
                                            No Permission Tracking Data
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            This user has no permission tracking records.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Drawer>
    );
};
